import api from "./api";

// ====== TIPOS DE DATOS ADMIN ======

export interface DashboardMetrics {
  sales: {
    today: number;
    month: number;
    total: number;
    growth: number; // Porcentaje de crecimiento
  };
  orders: {
    today: number;
    pending: number;
    processing: number;
    completed: number;
    total: number;
  };
  products: {
    total: number;
    active: number;
    lowStock: number;
    outOfStock: number;
  };
  users: {
    total: number;
    active: number;
    newThisMonth: number;
  };
}

export interface SalesReportData {
  date: string;
  sales: number;
  orders: number;
  revenue: number;
}

export interface SalesReport {
  data: SalesReportData[];
  summary: {
    totalSales: number;
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
  };
  period: {
    startDate: string;
    endDate: string;
    groupBy: "day" | "week" | "month";
  };
}

export interface TopProduct {
  productId: string;
  title: string;
  image: string;
  totalSold: number;
  revenue: number;
  averageRating: number;
}

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: "cliente" | "admin";
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  ordersCount: number;
  totalSpent: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface AdminOrder {
  _id: string;
  orderNumber: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  items: Array<{
    product: {
      _id: string;
      title: string;
      image: string;
      type: "físico" | "digital";
    };
    quantity: number;
    price: number;
  }>;
  totalItems: number;
  subtotal: number;
  shipping: number;
  total: number;
  status: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
  paymentMethod: string;
  paymentStatus: "pending" | "completed" | "failed";
  createdAt: string;
  updatedAt: string;
}

export interface AdminOrdersResponse {
  orders: AdminOrder[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalOrders: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface PaymentSettings {
  provider: string;
  isActive: boolean;
  paguelofacil: {
    apiKey: string;
    cclw: string;
    sandboxApiKey: string;
    sandboxCclw: string;
    useSandbox: boolean;
  };
  currentEnvironment: {
    mode: "sandbox" | "production";
    urls: {
      baseUrl: string;
      apiUrl: string;
    };
  };
  updatedAt: string;
}

export interface OrderFilters {
  status?: string;
  paymentStatus?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UserFilters {
  role?: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

// ====== SERVICIO ADMIN ======

class AdminService {
  // ====== DASHBOARD Y MÉTRICAS ======

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const response = await api.get("/admin/dashboard");
    return response.data.data;
  }

  async getSalesReport(filters: {
    startDate?: string;
    endDate?: string;
    groupBy?: "day" | "week" | "month";
  }): Promise<SalesReport> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.groupBy) params.append("groupBy", filters.groupBy);

    const response = await api.get(`/admin/reports/sales?${params.toString()}`);
    return response.data.data;
  }

  async getTopProducts(limit: number = 10): Promise<TopProduct[]> {
    const response = await api.get(
      `/admin/reports/top-products?limit=${limit}`
    );
    return response.data.data;
  }

  // ====== GESTIÓN DE ÓRDENES ======

  async getAllOrders(filters?: OrderFilters): Promise<AdminOrdersResponse> {
    const params = new URLSearchParams();

    if (filters?.status) params.append("status", filters.status);
    if (filters?.paymentStatus)
      params.append("paymentStatus", filters.paymentStatus);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.userId) params.append("userId", filters.userId);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const response = await api.get(`/admin/orders?${params.toString()}`);
    return {
      orders: response.data.data,
      pagination: response.data.pagination,
    };
  }

  async getOrderDetails(orderId: string): Promise<AdminOrder> {
    const response = await api.get(`/admin/orders/${orderId}`);
    return response.data.data;
  }

  async updateOrderStatus(
    orderId: string,
    status: string,
    note?: string
  ): Promise<AdminOrder> {
    const response = await api.put(`/admin/orders/${orderId}/status`, {
      status,
      note,
    });
    return response.data.data;
  }

  async markOrderAsPaid(
    orderId: string,
    paymentData?: { transactionId?: string; paymentMethod?: string }
  ): Promise<AdminOrder> {
    const response = await api.post(
      `/admin/orders/${orderId}/mark-paid`,
      paymentData || {}
    );
    return response.data.data;
  }

  async sendDeliveryCoordination(orderId: string): Promise<AdminOrder> {
    const response = await api.post(
      `/admin/orders/${orderId}/send-delivery-coordination`
    );
    return response.data.data;
  }

  async exportOrders(filters?: OrderFilters): Promise<void> {
    const orders = await this.getAllOrders({ ...filters, limit: 1000 });
    const exportData = orders.orders.map((order) => ({
      "Número de Orden": order.orderNumber,
      Cliente: order.user.name,
      Email: order.user.email,
      Estado: order.status,
      "Estado de Pago": order.paymentInfo.status,
      Total: order.total,
      Productos: order.totalItems,
      Fecha: new Date(order.createdAt).toLocaleDateString("es-ES"),
      Dirección: order.shippingInfo.address,
      Ciudad: order.shippingInfo.city,
      Teléfono: order.shippingInfo.phone,
    }));

    this.exportToCSV(
      exportData,
      `ordenes-${new Date().toISOString().split("T")[0]}`
    );
  }

  // ====== GESTIÓN DE USUARIOS ======

  async getAllUsers(filters?: UserFilters): Promise<AdminUsersResponse> {
    const params = new URLSearchParams();

    if (filters?.role) params.append("role", filters.role);
    if (filters?.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const response = await api.get(`/admin/users?${params.toString()}`);
    return {
      users: response.data.data,
      pagination: response.data.pagination,
    };
  }

  async updateUserRole(
    userId: string,
    role: "cliente" | "admin"
  ): Promise<AdminUser> {
    const response = await api.put(`/admin/users/${userId}/role`, { role });
    return response.data.data;
  }

  // ====== CONFIGURACIÓN DE PAGOS ======

  async getPaymentSettings(): Promise<PaymentSettings | null> {
    const response = await api.get("/admin/payment-settings");
    return response.data.data;
  }

  async updatePaymentSettings(settings: {
    apiKey?: string;
    cclw?: string;
    sandboxApiKey?: string;
    sandboxCclw?: string;
    useSandbox: boolean;
  }): Promise<PaymentSettings> {
    const response = await api.put("/admin/payment-settings", settings);
    return response.data.data;
  }

  async testPaymentConnection(): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await api.post("/admin/payment-settings/test");
    return response.data.data;
  }

  // ====== UTILIDADES ======

  getStatusColor(status: AdminOrder["status"]): string {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      processing: "bg-blue-100 text-blue-800 border-blue-300",
      shipped: "bg-purple-100 text-purple-800 border-purple-300",
      delivered: "bg-green-100 text-green-800 border-green-300",
      cancelled: "bg-red-100 text-red-800 border-red-300",
    };
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-300";
  }

  getStatusLabel(status: AdminOrder["status"]): string {
    const labels = {
      pending: "Pendiente",
      processing: "Procesando",
      shipped: "Enviado",
      delivered: "Entregado",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  }

  getPaymentStatusColor(status: AdminOrder["paymentStatus"]): string {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  }

  getPaymentStatusLabel(status: AdminOrder["paymentStatus"]): string {
    const labels = {
      pending: "Pendiente",
      completed: "Completado",
      failed: "Fallido",
    };
    return labels[status] || status;
  }

  formatDateRange(startDate: string, endDate: string): string {
    const start = new Date(startDate).toLocaleDateString("es-ES");
    const end = new Date(endDate).toLocaleDateString("es-ES");
    return `${start} - ${end}`;
  }

  calculateGrowthPercentage(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  exportToCSV(data: Record<string, unknown>[], filename: string): void {
    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers.map((header) => `"${row[header] || ""}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export default new AdminService();
